'use client'

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Link as LinkIcon, 
  QrCode, 
  Globe, 
  ShoppingBag, 
  Users, 
  Zap, 
  Shield, 
  BarChart3, 
  Lock, 
  Clock, 
  MousePointerClick, 
  Plus, 
  Gift, 
  Eye, 
  Crosshair, 
  CheckCircle2, 
  MoveRight,
  Monitor,
  Smartphone,
  Check,
  AlertTriangle,
  RefreshCcw,
  FileSpreadsheet,
  Download,
  ExternalLink,
  Wand2,
  Camera,
  Mail,
  Briefcase,
  ArrowRight,
  ClipboardCopy,
  ChevronDown,
  Terminal,
  Code,
  Database,
  Film,
  Sparkles,
  Hash,
  Search
} from 'lucide-react'
import Link from 'next/link'
import { useUser } from '@clerk/nextjs'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { GlowingEffect } from '@/components/ui/glowing-effect'
import ThreeDMarqueeDemo from '@/components/3d-marquee-demo'
import Bucket from '@/components/ui/bucket'

const categories = [
  {
    title: "Smart Link Management",
    description: "Go beyond simple shortening with programmable routing and deep analytics.",
    icon: LinkIcon,
    features: [
      { name: "Programmable Links", description: "Multi-destination smart routing based on custom conditions.", icon: Wand2 },
      { name: "Deep Analytics", description: "Real-time tracking, device stats, and geographic data.", icon: BarChart3 },
      { name: "Advanced Security", description: "Password protection, expiring links, and geo-blocking.", icon: Shield },
      { name: "Device Targeting", description: "iOS, Android, and Desktop specific overrides.", icon: Smartphone },
      { name: "Health Monitoring", description: "Automated broken link detection and reporting.", icon: RefreshCcw },
      { name: "Mass Import / Export", description: "Bulk management using CSV for massive link libraries.", icon: FileSpreadsheet },
      { name: "Custom Slugs", description: "Personalized, branded aliases for every link.", icon: LinkIcon },
      { name: "Meta Customization", description: "Control titles and descriptions for social sharing.", icon: Eye },
    ]
  },
  {
    title: "Dynamic QR Codes",
    description: "Update destinations instantly without reprinting your codes.",
    icon: QrCode,
    features: [
      { name: "Dynamic Routing", description: "Change QR code destinations in real-time from the dashboard.", icon: Zap },
      { name: "Mass QR Creation", description: "Generate hundreds of personalized QR codes instantly via CSV.", icon: FileSpreadsheet },
      { name: "High-Res Exports", description: "Download crisp QR codes for digital and print use.", icon: Download },
      { name: "Brand Mapping", description: "Map QR codes to your custom branded subdomains.", icon: ExternalLink },
    ]
  },
  {
    title: "Link in Bio & Reels to Link",
    description: "Create stunning landing pages for your social profiles with interactive #codes and reel thumbnails.",
    icon: Globe,
    features: [
      { name: "Reel-to-Link Codes", description: "Assign short #codes to any link so followers find content instantly.", icon: Film },
      { name: "Visual Reels Grid", description: "Mirror your social feed with an 'As Seen on Reels' thumbnail grid.", icon: Camera },
      { name: "Deep-Link Routing", description: "Auto-spotlight exact links using ?code=24 URL parameters.", icon: Sparkles },
      { name: "Mobile Themes", description: "Premium, responsive designs for every niche.", icon: Monitor },
      { name: "Custom Branding", description: "Flexible colors, link styles, and bio layouts.", icon: Wand2 },
      { name: "Social Integration", description: "Direct links to all your major social profiles.", icon: Users },
      { name: "Monetization Cards", description: "Embed stores and support buttons directly.", icon: ShoppingBag },
      { name: "Bio SEO", description: "Optimized discovery tools for your bio pages.", icon: Globe },
    ]
  },
  {
    title: "Monetization & Growth",
    description: "Turn your audience into a thriving business.",
    icon: ShoppingBag,
    features: [
      { name: "Digital Store", description: "Sell files, guides, and services directly.", icon: FileSpreadsheet },
      { name: "Creator Sponsorship", description: "Support buttons with custom titles and links.", icon: HeartIcon },
      { name: "Referral Engine", description: "Built-in tracking for viral growth campaigns.", icon: Gift },
      { name: "Fraud & Bot Protection", description: "Advanced anti-fraud filters that analyze IP reuse, rapid submissions, and email pattern indicators.", icon: Shield },
      { name: "Product Spotlights", description: "Highlight items on your redirect/preview gates.", icon: SparklesIcon },
      { name: "Campaign Snippets", description: "Store and reuse high-converting cold DMs, captions, and outreach comments.", icon: ClipboardCopy },
    ]
  },
  {
    title: "Advanced Strategy",
    description: "Tools for marketers who demand deeper insights.",
    icon: Crosshair,
    features: [
      { name: "Multi-Platform Pixels", description: "FB, Google, LinkedIn, TikTok, and more pixel support.", icon: Crosshair },
      { name: "Custom Scripts", description: "Inject tracking code for bespoke analytics.", icon: Wand2 },
      { name: "Global Fallbacks", description: "Set backup destinations for expired links.", icon: RefreshCcw },
      { name: "Preview Gates", description: "Optional redirect timers for higher engagement.", icon: Clock },
    ]
  },
  {
    title: "Enterprise & API",
    description: "Scalable infrastructure and collaboration tools for teams.",
    icon: Shield,
    features: [
      { name: "Developer API", description: "Integrate Lenklyst directly into your own app or workflow.", icon: Briefcase },
      { name: "Team Collaboration", description: "Invite members with Admin, Editor, and Viewer roles.", icon: Users },
      { name: "Custom Domains", description: "Use your own branded subdomains for total brand control.", icon: Globe },
      { name: "Session Security", description: "View and revoke active logins for your team ecosystem.", icon: Lock },
    ]
  },
  {
    title: "Headless Forms",
    description: "Build custom forms anywhere, submit data directly to us securely.",
    icon: Terminal,
    features: [
      { name: "Frontend Freedom", description: "Use React, Vue, HTML. Build forms your way.", icon: Code },
      { name: "Domain Verification", description: "Keyless security. Lock endpoints to specific domains via CORS.", icon: Lock },
      { name: "Spam Filtering", description: "Built-in rate limiting and invisible honeypots.", icon: Shield },
      { name: "Raw Data Inbox", description: "Accept deeply nested JSON payloads or file uploads natively.", icon: Database },
      { name: "Webhook Triggers", description: "Forward incoming data automatically to Zapier or Make.", icon: Zap },
    ]
  }
]

function HeartIcon(props: any) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
  )
}

function SparklesIcon(props: any) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>
  )
}

interface FAQItem {
  category: string
  question: string
  answer: string
}

const faqs: FAQItem[] = [
  // Links Section (3 questions)
  {
    category: "Links",
    question: "How do programmable links and routing work?",
    answer: "Programmable links allow you to redirect visitors based on custom conditions such as their device type (iOS, Android, Desktop), geographical location, browser language, or specific scheduling parameters. This ensures your target audience gets redirected to the most relevant landing page or app store automatically."
  },
  {
    category: "Links",
    question: "Can I password protect or set expiration dates on links?",
    answer: "Yes. For sensitive content, private downloads, or time-sensitive offers, you can add password protection to redirect gates or set links to expire after a certain date or a specific number of total clicks."
  },
  {
    category: "Links",
    question: "What is bulk link management and CSV importing?",
    answer: "If you have a large library of links, you can import them in bulk using a CSV file. You can also export your links and their performance statistics at any time for offline analysis or migration."
  },
  // QR Codes Section (3 questions)
  {
    category: "QR Codes",
    question: "What are dynamic QR codes, and can I change their destination after printing?",
    answer: "Yes! Unlike static QR codes, dynamic QR codes point to a redirect URL managed by our platform. This means you can update the destination URL at any time from your dashboard in real-time, without ever needing to regenerate or reprint the physical QR code."
  },
  {
    category: "QR Codes",
    question: "In what file formats can I download generated QR codes?",
    answer: "You can download your QR codes in high-resolution digital formats suitable for both web and print production, including standard PNG and scaleable SVG formats."
  },
  {
    category: "QR Codes",
    question: "Can I generate QR codes in bulk?",
    answer: "Yes. Our platform supports bulk QR code generation. By uploading a list of URLs in a CSV file, you can instantly generate and download hundreds of custom QR codes in a single operation."
  },
  // Bio Pages Section (3 questions)
  {
    category: "Bio Pages",
    question: "How customizable are the Link-in-Bio pages?",
    answer: "Our Link-in-Bio pages are highly customizable with premium responsive themes, custom branding, and monetization integration. You can choose layout styles, custom backgrounds, fonts, button styling, and embed digital stores or sponsorship links directly."
  },
  {
    category: "Bio Pages",
    question: "Can I sell digital products directly on my bio page?",
    answer: "Yes. You can add monetization cards to sell digital downloads, guides, services, or set up support buttons (e.g. buy me a coffee) that collect payments directly from your audience."
  },
  {
    category: "Bio Pages",
    question: "How does the Reel-to-Link & Code feature work?",
    answer: "Lenklyst lets you assign short memorable codes (like #24 or #PRESET) and upload reel cover thumbnails to your bio page. When viewers tap your bio link, an instant search bar and visual 2-column reel grid spotlight the exact destination link in 1 tap."
  },
  {
    category: "Bio Pages",
    question: "Can I upload custom thumbnail covers for my reels?",
    answer: "Yes! You can upload custom video cover thumbnails directly from your dashboard or paste direct image URLs. They appear in an 'As Seen on Reels' visual grid on your public bio page so visitors can visually match what they saw in your video."
  },
  {
    category: "Bio Pages",
    question: "Is there a draft mode for bio pages?",
    answer: "Yes, you can edit your layout, add new elements, and preview your changes in a mobile layout draft mode before publishing them live to your public link-in-bio URL."
  },
  // Analytics Section (3 questions)
  {
    category: "Analytics",
    question: "Does the platform support tracking pixels and custom integrations?",
    answer: "Absolutely. You can easily attach UTM parameters for campaign tracking, integrate tracking pixels (Facebook, Google, LinkedIn, TikTok, etc.) to build retargeting audiences, and export data in CSV/PDF formats or connect via our developer API."
  },
  {
    category: "Analytics",
    question: "What kind of visitor data is tracked in real-time?",
    answer: "Our analytics track referrer sources, click counts, unique visitors, device types, operating systems, browser languages, and geographic locations (country and city levels) while maintaining full GDPR/CCPA privacy compliance."
  },
  {
    category: "Analytics",
    question: "How does broken link health monitoring work?",
    answer: "The platform automatically monitors the destination targets of your active links. If a destination page returns a 404 or becomes unavailable, you will receive an alert or redirect traffic to a designated fallback page."
  },
  // Referrals Section (3 questions)
  {
    category: "Referrals",
    question: "What is the Referral Engine, and how does it drive viral growth?",
    answer: "The Referral Engine allows you to run campaigns where your users can share unique referral links. You can track sign-ups and award benefits, encouraging organic sharing and word-of-mouth growth for your product or brand."
  },
  {
    category: "Referrals",
    question: "How does postback conversion tracking work for referrals?",
    answer: "You can integrate webhook postbacks or JavaScript conversion tags on your checkout or sign-up pages. When a referred user completes an action, our system registers the conversion and associates it with the referrer."
  },
  {
    category: "Referrals",
    question: "Can I set up fraud detection for referral campaigns?",
    answer: "Yes, the platform includes advanced anti-fraud filters that analyze IP reuse, rapid submissions, and email pattern indicators to prevent users from gaming your referral incentives."
  },
  // Domains Section (3 questions)
  {
    category: "Domains",
    question: "Can I use my own custom domain for shortened links and bio pages?",
    answer: "Yes, you can configure your custom domains and subdomains (e.g. link.yourbrand.com) to serve both shortened links and bio pages. This keeps your brand front-and-center, increases trust, and improves click-through rates."
  },
  {
    category: "Domains",
    question: "How do I point my domain to your platform?",
    answer: "To map a custom subdomain, you simply create a CNAME record in your DNS settings (e.g., Cloudflare, GoDaddy, Namecheap) pointing to our server. We automatically generate and renew SSL certificates for your domains."
  },
  {
    category: "Domains",
    question: "Is there a limit to how many custom domains I can connect?",
    answer: "The number of custom domains depends on your subscription plan. Starter plans support a limited number of custom subdomains, while Agency and Enterprise tiers allow you to link multiple custom root domains and subdomains."
  },
  // Headless Forms Section (3 questions)
  {
    category: "Headless Forms",
    question: "Do I need to include an API key in my HTML form?",
    answer: "No! Headless Forms use Domain Verification (CORS) for security. You simply whitelist your website's domain in the dashboard, and we will reject any submissions that come from unauthorized websites. Your frontend code remains 100% safe."
  },
  {
    category: "Headless Forms",
    question: "Can I automatically redirect users after they submit an HTML form?",
    answer: "Yes, you can easily set a default redirect URL in your dashboard, or pass a hidden '_redirect' input field inside your HTML form. We will seamlessly process the data and issue a 302 redirect to your custom thank-you page."
  },
  {
    category: "Headless Forms",
    question: "What frontend frameworks are supported?",
    answer: "All of them. Because the API simply accepts standard POST requests (JSON, multipart/form-data, or x-www-form-urlencoded), you can build your forms using React, Vue, Angular, Svelte, or plain vanilla HTML."
  }
]

export default function FeaturesClient() {
  const { isSignedIn: session } = useUser()
  const [openIndex, setOpenIndex] = React.useState<number | null>(null)
  const [selectedCategory, setSelectedCategory] = React.useState<string>('All')

  const filteredFaqs = selectedCategory === 'All'
    ? faqs
    : faqs.filter(faq => faq.category === selectedCategory)

  return (
    <div className="relative min-h-screen bg-black overflow-hidden selection:bg-primary/30">
      <GeometricBackground />
      <MarketingNavbar />

      <main className="relative z-10 pt-36 pb-24">
        {/* Header Section */}
        <section className="relative px-6 lg:px-12 text-center mb-20 md:mb-40 overflow-hidden">
          {/* Background Marquee */}
          <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden mask-fade-out" style={{ perspective: '1000px' }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[45%] w-[120%] h-[150%] opacity-50">
              <ThreeDMarqueeDemo />
            </div>
          </div>

          <div className="relative z-10 max-w-[1400px] 2xl:max-w-[1800px] mx-auto py-12 md:py-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter text-white uppercase mb-6">
                Every tool you <span className="text-primary">need.</span>
              </h1>
              <p className="text-xl text-white/50 max-w-2xl mx-auto font-light mb-8">
                Your complete marketing and branding ecosystem. One platform to replace the dozen tools you juggle, so you can focus on growing.
              </p>
              
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link 
                  id="features-hero-cta"
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 py-4 sm:px-8 sm:py-4 bg-primary text-white rounded-full font-bold text-sm sm:text-base hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(0,102,255,0.4)]"
                >
                  <span className="text-center">{session ? 'Open Dashboard' : 'Start for Free'}</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  id="features-compare-btn"
                  href="/pricing" 
                  className="px-8 py-4 bg-white/5 border border-white/10 text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-all"
                >
                  Compare Plans
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Content */}
        <section className="relative pt-20 pb-32">
          <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
            <div className="space-y-20 md:space-y-32">
              {categories.map((cat, idx) => {
                const words = cat.title.split(' ');
                const firstPart = words.slice(0, words.length > 2 ? 2 : 1).join(' ');
                const secondPart = words.slice(words.length > 2 ? 2 : 1).join(' ');
                
                return (
                  <div key={cat.title} className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 md:gap-12 lg:gap-24 items-start`}>
                    
                    {/* Category Sticky Header */}
                    <div className="w-full lg:w-1/3 lg:sticky lg:top-32">
                      <motion.div
                        initial={{ opacity: 0, x: idx % 2 === 1 ? 20 : -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="space-y-6"
                      >
                        <div>
                            <h2 className="text-3xl lg:text-5xl font-bold tracking-tighter uppercase text-white mb-4">
                              {firstPart}<br /><span className="text-primary">{secondPart}.</span>
                            </h2>
                            <p className="text-white/50 text-lg lg:text-xl font-light leading-relaxed">
                            {cat.description}
                            </p>
                        </div>
                      </motion.div>
                    </div>

                    {/* Features Animation */}
                    <div className="w-full lg:w-2/3">
                      <div className="w-full flex justify-center py-6 md:py-12">
                        <Bucket items={cat.features} />
                      </div>
                    </div>

                  </div>
                )
              })}
            </div>

            {/* FAQ Section */}
            <section className="relative mt-24 md:mt-40 pt-20 pb-12 border-t border-white/5">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 items-start">
                
                {/* Left Column: Title and Filters */}
                <div className="w-full lg:w-1/3 lg:sticky lg:top-32 space-y-6">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                  >
                    <h2 className="text-3xl lg:text-5xl font-bold tracking-tighter uppercase text-white mb-4">
                      Got <span className="text-primary">questions?</span><br />We have answers.
                    </h2>
                    <p className="text-white/50 text-base lg:text-lg font-light leading-relaxed mb-6">
                      Find quick answers to common questions about our platform features, tools, integrations, and capabilities.
                    </p>
                    
                    {/* Category Pill Filters */}
                    <div className="flex flex-wrap gap-2">
                      {['All', 'Links', 'QR Codes', 'Bio Pages', 'Analytics', 'Referrals', 'Domains', 'Headless Forms'].map((cat) => (
                        <button
                          key={cat}
                          onClick={() => {
                            setSelectedCategory(cat)
                            setOpenIndex(null) // Reset accordion state when category changes
                          }}
                          className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-200 border cursor-pointer ${
                            selectedCategory === cat
                              ? 'bg-primary border-primary text-white shadow-[0_0_15px_rgba(0,102,255,0.3)]'
                              : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Right Column: Accordion */}
                <div className="w-full lg:w-2/3">
                  <div className="space-y-4">
                    {filteredFaqs.map((faq, index) => {
                      const isOpen = openIndex === index;
                      return (
                        <motion.div
                          key={faq.question}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.05 }}
                          className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                            isOpen 
                              ? 'border-primary/40 bg-gradient-to-br from-primary/5 via-black to-black shadow-[0_0_30px_rgba(0,102,255,0.05)]' 
                              : 'border-white/5 bg-white/[0.01] hover:border-white/10 hover:bg-white/[0.03]'
                          }`}
                        >
                          <button
                            onClick={() => setOpenIndex(isOpen ? null : index)}
                            className="w-full flex items-center justify-between p-6 text-left focus:outline-none cursor-pointer"
                          >
                            <div className="space-y-2 pr-4">
                              <span className="inline-block text-[10px] font-bold tracking-widest text-primary uppercase bg-primary/10 px-2 py-0.5 rounded">
                                {faq.category}
                              </span>
                              <h3 className="text-lg font-semibold text-white tracking-tight">
                                {faq.question}
                              </h3>
                            </div>
                            <div className={`p-2 rounded-full bg-white/5 border border-white/10 transition-transform duration-300 flex-shrink-0 ${isOpen ? 'rotate-180 text-primary border-primary/20 bg-primary/10' : 'text-white/60'}`}>
                              <ChevronDown className="w-5 h-5" />
                            </div>
                          </button>

                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                                className="overflow-hidden"
                              >
                                <div className="p-6 pt-0 text-white/60 leading-relaxed text-sm font-light border-t border-white/5 mt-4">
                                  {faq.answer}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      )
                    })}
                  </div>
                </div>

              </div>
            </section>

            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-20 md:mt-40 relative rounded-3xl border border-primary/20 bg-primary/5 p-8 md:p-12 lg:p-24 text-center overflow-hidden"
            >
              <div className="absolute inset-0 bg-primary/5 blur-[100px] pointer-events-none" />
              <div className="relative z-10 max-w-2xl mx-auto space-y-8">
                <h2 className="text-4xl lg:text-6xl font-bold tracking-tighter uppercase text-white">
                  Start for <span className="text-primary">free.</span>
                </h2>
                <p className="text-xl text-white/50 font-light">
                  Be among the first creators and businesses to run their entire marketing from one powerful platform.
                </p>
                <Link
                  id="features-bottom-cta"
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="group w-full sm:w-auto mx-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 py-4 sm:px-10 sm:py-5 bg-primary text-white rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_60px_rgba(0,102,255,0.4)]"
                >
                  <span className="text-center">{session ? 'Open Dashboard' : 'Start for Free'}</span>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>

          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  )
}
